/* ============================================================
 * <lion-cookie-banner> — Shopify customerPrivacy API integration.
 * Supports bar, floating, and modal display modes,
 * a per-category preferences panel, and an external re-open trigger.
 * ============================================================ */

const CONSENT_STORAGE_KEY = 'cookie-consent';
const CONSENT_EVENT = 'cookie-consent-changed';

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeStoredConsent(consent) {
  try { localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent)); } catch (_) {}
}

class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.acceptBtn = this.querySelector('[data-cookie-accept]');
    this.rejectBtn = this.querySelector('[data-cookie-reject]');
    this.manageBtn = this.querySelector('[data-cookie-manage]');
    this.savePrefsBtn = this.querySelector('[data-cookie-save]');
    this.prefsPanel = this.querySelector('[data-cookie-prefs]');
    this.overlayEl = this.querySelector('[data-cookie-overlay]');
    this.isModal = this.dataset.mode === 'modal';
    this.categories = (this.dataset.categories || 'analytics,marketing')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;
    // Always preview in the theme editor so merchants can style it.
    if (this.dataset.designMode === 'true') {
      this._showBanner({ skipFocus: true });
      return;
    }
    this._bindReopenTriggers();
    this._init();
  }

  _bindReopenTriggers() {
    document.querySelectorAll('[data-cookie-reopen]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this._showBanner();
      });
    });
  }

  _init() {
    if (window.Shopify?.customerPrivacy) {
      this._onPrivacyReady();
      return;
    }
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      // API not available — fall back to local storage decision.
      readStoredConsent() ? this._applyConsent(readStoredConsent()) : this._showBanner();
      return;
    }
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (err) => {
        if (err) {
          readStoredConsent() ? this._applyConsent(readStoredConsent()) : this._showBanner();
          return;
        }
        this._onPrivacyReady();
      }
    );
  }

  _readPrivacyConsent() {
    const api = this.privacy;
    if (!api) return null;
    try {
      if (typeof api.currentVisitorConsent === 'function') return api.currentVisitorConsent();
      // Legacy fallback for older API surface.
      if (api.visitorConsent) return api.visitorConsent;
    } catch (_) {}
    return null;
  }

  _onPrivacyReady() {
    this.privacy = window.Shopify.customerPrivacy;
    const consent = this._readPrivacyConsent();
    // Visitor has not interacted yet when every category is "no_interaction" or undefined.
    const needsChoice = !consent || Object.keys(consent).length === 0 ||
      Object.values(consent).every((v) => v === 'no_interaction' || v == null);
    if (needsChoice) {
      this._showBanner();
    } else {
      this._applyConsent(consent);
    }
  }

  _showBanner({ skipFocus = false } = {}) {
    if (this._visible) return;
    this._visible = true;
    this.hidden = false;
    if (this.isModal) document.body.classList.add('cookie-banner-open');
    requestAnimationFrame(() => this.classList.add('is-visible'));

    if (!this._listenersBound) {
      this._listenersBound = true;
      this.acceptBtn?.addEventListener('click', this._onAccept.bind(this));
      this.rejectBtn?.addEventListener('click', this._onReject.bind(this));
      this.manageBtn?.addEventListener('click', this._onManageToggle.bind(this));
      this.savePrefsBtn?.addEventListener('click', this._onSavePrefs.bind(this));
    }
    if (!skipFocus) this.acceptBtn?.focus();
  }

  _hideBanner() {
    this._visible = false;
    this.classList.remove('is-visible');
    document.body.classList.remove('cookie-banner-open');
    const finish = () => {
      this.hidden = true;
      this.dispatchEvent(new CustomEvent('cookie-banner-closed', { bubbles: true }));
    };
    let done = false;
    this.addEventListener('transitionend', () => { if (!done) { done = true; finish(); } }, { once: true });
    // Safety net in case no transition fires.
    setTimeout(() => { if (!done) { done = true; finish(); } }, 450);
  }

  _onAccept() {
    const consent = this._fullConsent(true);
    this._setConsent(consent);
    this._hideBanner();
  }

  _onReject() {
    const consent = this._fullConsent(false);
    this._setConsent(consent);
    this._hideBanner();
  }

  _onManageToggle() {
    if (!this.prefsPanel) return;
    const willOpen = this.prefsPanel.hidden;
    this.prefsPanel.hidden = !willOpen;
    this.classList.toggle('is-managing', willOpen);
    this.manageBtn?.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  }

  _onSavePrefs() {
    const consent = {};
    this.categories.forEach((cat) => {
      const input = this.prefsPanel?.querySelector(`[data-cookie-pref="${cat}"]`);
      consent[cat] = input ? !!input.checked : false;
    });
    this._setConsent(consent);
    this._hideBanner();
  }

  _fullConsent(value) {
    const consent = {};
    this.categories.forEach((c) => { consent[c] = value; });
    return consent;
  }

  _setConsent(consent) {
    if (this.privacy?.setTrackingConsent) {
      try {
        this.privacy.setTrackingConsent(consent, () => {});
      } catch (_) {
        // Older API: pass a single boolean as last resort.
        const anyTrue = Object.values(consent).some(Boolean);
        try { this.privacy.setTrackingConsent(anyTrue, () => {}); } catch (_) {}
      }
    }
    this._applyConsent(consent);
  }

  _applyConsent(consent) {
    writeStoredConsent(consent);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
  }
}

if (!customElements.get('lion-cookie-banner')) customElements.define('lion-cookie-banner', CookieBanner);
